import { api } from '../../../core/network/api';

/**
 * The referral link's two halves: the code a player shares, and the code a player arrived with.
 *
 * **Why the capture runs at module load.** A referral link is `https://…/?ref=CODE`, and the
 * visitor who follows it is signed out, so the code has to survive a landing page, a register
 * form, an email verification and a login before anything can claim it. It cannot survive the
 * URL: `main.ts`'s `navigate()` is `history.pushState(null, '', to)` with no query string, so
 * the first hop after sign-in (`navigate('/play')`) drops `?ref=` on the floor.
 *
 * So the capture has to happen before the router's first `render()`. `main.ts` is not a file
 * this screen owns, but it statically imports `mountShell` → `route-table.ts` →
 * `settings-route.ts` → this module, and ES module side effects run before the importing
 * module's body. The bare `captureReferralCode()` call at the bottom of this file is therefore
 * "boot time" in the only sense that matters here, without editing a file outside this feature.
 *
 * Claiming is deliberately NOT automatic. The sibling game claims silently on first authenticated
 * screen and swallows every failure, which means a player whose claim was refused (unknown code,
 * their own link, an account older than the window, a code already claimed) is never told — the
 * bonus simply never arrives and there is nothing to read. Here the pending code surfaces on the
 * Referral tab as a one-tap claim, and the server's own message is shown verbatim when it says no.
 */

const PENDING_KEY = 'blackline.referral.pending';

/** `GET /referral/me`. */
export interface ReferralInfo {
  /** This player's own code — the backend generates it lazily on first read. */
  code: string;
  /** How many accounts have signed up through this player's link. */
  referredCount: number;
  /** Whether this player has already claimed someone else's code. One per account, ever. */
  claimed: boolean;
  /** Credits paid for a successful claim. Read from the server so the copy can never drift
   * from the economy constant behind it. */
  reward: number;
}

export const fetchReferralInfo = () => api.get<ReferralInfo>('/referral/me');

/** `GET /referral/status` — is the programme switched on in the admin dashboard? */
export interface ReferralStatus {
  enabled: boolean;
  reward: number;
}

export const fetchReferralStatus = () => api.get<ReferralStatus>('/referral/status');

export const claimReferral = (code: string) =>
  api.post<{ ok: true; reward: number }>('/referral/claim', { code });

/**
 * Reads `?ref=CODE` out of the URL, stashes it, and strips the parameter.
 *
 * Stripping matters twice over: it keeps the code out of a URL the player might bookmark or
 * paste into chat, and it stops a later capture from resurrecting a code the player already
 * decided against. Any other query parameter and the hash are preserved.
 *
 * Storage is best-effort. A browser with site data blocked throws on `localStorage`, and a
 * referral bonus is not worth taking the shell down for.
 */
export function captureReferralCode(): void {
  if (typeof location === 'undefined') return;
  let code: string | null = null;
  let rest = '';
  try {
    const params = new URLSearchParams(location.search);
    code = params.get('ref');
    if (!code) return;
    params.delete('ref');
    rest = params.toString();
  } catch {
    return;
  }
  const trimmed = code.trim().toUpperCase();
  try {
    if (trimmed) localStorage.setItem(PENDING_KEY, trimmed);
  } catch {
    // No storage — the code is lost, which is strictly better than a crashed boot.
  }
  try {
    history.replaceState(null, '', location.pathname + (rest ? `?${rest}` : '') + location.hash);
  } catch {
    // `replaceState` can throw in a sandboxed frame; the stashed code is already safe.
  }
}

/** The code this player arrived with and has not yet claimed, or null. */
export function pendingReferralCode(): string | null {
  try {
    const code = localStorage.getItem(PENDING_KEY);
    return code && code.trim() ? code.trim() : null;
  } catch {
    return null;
  }
}

/**
 * Forgets the pending code.
 *
 * Called after a claim resolves either way. A refusal is as final as a success: every reason the
 * server refuses (unknown, self, expired window, already claimed) is permanent for this account,
 * so keeping the code would only offer a button that can never work.
 */
export function clearPendingReferral(): void {
  try {
    localStorage.removeItem(PENDING_KEY);
  } catch {
    // Nothing to clear if storage is unavailable.
  }
}

/** The link a player shares. Built from the live origin rather than a configured APP_URL so a
 * sandbox deploy hands out sandbox links instead of production ones. */
export function referralLinkFor(code: string): string {
  const origin = typeof location !== 'undefined' ? location.origin : '';
  return `${origin}/?ref=${encodeURIComponent(code)}`;
}

captureReferralCode();
